import React, { useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import {
  createInteractivePageSection,
  updateWidgetProperty,
  toggleEditMode,
  selectPageWidget,
  createPageSegment,
  actionGroup,
  deleteWidgets,
  arrangeWidget,
  clonePage,
  createWidget,
} from "actions/editor";

import _ from "lodash";
import cx from "classnames";
import { Frame } from 'scenejs';

import { EditorContext } from '../../index';
import StoryboardContext from 'components/Storyboard/context';
import { Container } from '../core/container';
import { Button, Spinner, Image } from 'react-bootstrap';
import { Background } from 'components/Editor/components/properties/background';

import { Property } from '../../components/properties/';
import Tooltip from "components/Tooltip";
import { getBoundingRectFromLayout } from "components/Editor/helper/layoutManager";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import {
  useChildrenWidgetSelected,
  useContainerSelector,
  useHotspotWidgetSelector,
  useSelectedWidgetSelector,
  useWidgetSelector,
  useWidgetChildrenSelector,
  usePageWidgetSelector,
} from "components/Editor/hooks/useEditorSession";
import { Widget } from "./../../widgets/core";
import Portal from "components/Portal";
import { BackgroundView } from "../background";
import { PAGE, SECTION, SECTION_COLUMN } from "constants/widgetTypes";
import { useLocation, useRouteMatch } from "react-router-dom";
import { getMediaInfo } from "actions/pageHealth";
import { HeaderContext } from "pages/pages/components/header";
import { createPage } from "actions/pages";
import { getBaseUrl } from "services/util";
import { INTERACTIVE_CONTENT_TYPES, PAGE_CONTAIN_STORYBOARDS, SEGMENT_STORYBOARDS, STORYBOARD_TYPES } from "constants/storyboardType";
import smartGatingIcon from "../../../../assets/images/smartGatingBorder.svg";
import { desktopToMobileOptimize, mobileLayoutOptimize } from "actions/mobileOptimization";

import styles from "./page.module.scss";

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

export const PageWidgetContext = React.createContext({
  onResize: () => {},
});

const PageWidgetComponent = function ({
  className,
  style,
  widgetProps,
  widgetRef,
  children,
}) {
  const ref = widgetRef;

  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const headerContext = React.useContext(HeaderContext);
  
  const editor = editorContext.editorSession.id;
  const editorWidgetViewer = document.getElementsByClassName(
    "editor_widget_viewer"
  )[0];

  const pages = useSelector((state) => {
    const widgets = _.get(state, `${editor}.present.widgets`) || [];
    var pages = widgets.filter((item) => item.type === PAGE);
    pages = _.orderBy(pages, "order", "desc");
    return pages;
  });

  const storyboard = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboard, "type");
  const storyboardId = _.get(storyboard, 'id');
  const background = _.get(widgetProps, "properties.desktop.background");
  const entityType = useSelector((state) => _.get(state, `${editor}.present.entityType`));
  const breakpoint = useSelector((state) =>
    _.get(state, `${editor}.present.breakpoint`)
  );
  const isOptimizing = useSelector(state => _.get(state, `${editor}.present.mobileOptimization.pageWidgets.${widgetProps.uid}.optimizing`, false));
  const view = useSelector(state => {
    const mobile = _.get(
        state,
        `${editor}.present.mobileOptimization.pageWidgets.${widgetProps.uid}.view`,
        "desktop"
      ) === "mobile";
  
    return mobile ? "mobile" : "desktop";
  });  

  const id = _.get(widgetProps, "uid");
  const type = _.get(widgetProps, "type");

  const childrenSelection = useChildrenWidgetSelected(editor, id);
  const selectedWidget = useSelectedWidgetSelector(editor)?.[0];
  const visibleWitdh = _.get(
    widgetProps,
    `properties.${breakpoint}.container.minWidth`
  );
  const widgetHeight =
    parseInt(_.get(widgetProps, `properties.${breakpoint}.layout.height`)) || 0;

  const hotspotWidget = useHotspotWidgetSelector(editor, breakpoint);
  const hotspotWidgetParent = useWidgetSelector(
    editor,
    hotspotWidget && hotspotWidget?.parent
  );
  const hotspotVisibility = _.get(
    hotspotWidgetParent,
    `properties.${breakpoint}.visible`
  );

  const hotspotContainer = usePageWidgetSelector(editor, hotspotWidgetParent?.uid)
  const tools = useSelector((state) => _.get(state, `${editor}.present.tools`));
  const selected = useSelector((state) => {
    const selectedPage = _.get(state, `${editor}.present.selectedPage`, null);
    return selectedPage === id;
  });

  const offset = breakpoint === "desktop" ? 100 : 100;

  const [height, setHeight] = useState(`${widgetHeight}px`);

  const showToolbar = useSelector((state) => {
    const selected = _.get(state, `${editor}.present.selected`);
    return selected?.length === 0;
  });

  const isValidStorybaord = ["SPATIAL"].includes(storyboardType);
  const shouldShowOptimizeLoader = breakpoint === "mobile" && isOptimizing;
  const isSelectedWidgetValid =
    useContainerSelector(editor, _.get(selectedWidget, "uid"))?.type === PAGE &&
    _.get(selectedWidget, "type") !== SECTION;

  const [pageWidgetContext, setPageWidgetContext] = useState({
    onResize: () => {},
  });

  var widgetStyle = {
    ...style,
    width: `${visibleWitdh}px`,
    minHeight: `50px`,
  };

  useEffect(() => {
    // Resize the page height widget when the selected widget is deleted
    if (!selectedWidget && (storyboardType === "INTERACTIVE_CONTENT" || isValidStorybaord || entityType === "PALETTE_COMPONENT")) {
      handleResize();
    }
  }, [storyboardType, selectedWidget]);

  useEffect(() => {
    if (headerContext.optimizeLayout){
      handleResize();
    }

  }, [headerContext.optimizeLayout])

  React.useEffect(() => {
    setPageWidgetContext({
      onResize: handleResize,
    });
  }, []);

  React.useEffect(() => {
    const layout = _.get(widgetProps, `properties.${breakpoint}.layout`) || {};
    if (layout && layout.height != height) {
      dispatch(
        updateWidgetProperty(editor, id, breakpoint, {
          layout: {
            ...layout,
            height: height,
          },
        })
      );
    }
  }, [height]);

  const handleResize = () => {
    var height = 0;
    const parentRect = widgetRef.current.getBoundingClientRect();

    const editorWidgetViewer = document.getElementsByClassName(
      "editor_widget_viewer"
    )[0];

    const widgets = editorWidgetViewer.querySelectorAll(
      `#${id} .widget[data-widget-id]`
    );
    Array.from(widgets).forEach((element) => {
      const rect = element.getBoundingClientRect();
      height = Math.max(height, rect.bottom - parentRect.top);
    });

    height = Math.max(600, height);
    setHeight(`${height}px`);
  };

  const handleSelectPage = (e) => {
    if (selected) {
      return;
    }

    e.stopPropagation();
    e.preventDefault();

    dispatch(selectPageWidget(editor, id));
  };

  const handleAddSection =()=> {
    const desktopWidgetHeight = parseInt(_.get(widgetProps, `properties.desktop.layout.height`)) || 0;
    const mobileWidgetHeight = parseInt(_.get(widgetProps, `properties.mobile.layout.height`)) || 0;

    let sectionWidget = {
      name: "Section",
      type: SECTION,
      properties: {
        "desktop": {
          "visible": true,
          "layout": {
            "transform": {
              "translate": "0px, 0px",
              "rotate": "0deg"
            },
            "width": null,
            "height": `${desktopWidgetHeight}px`
          },
          "stretched": true,
          "margin": 0,
          "padding": 0,
          "columnSpacing": 0,
          "background": {
            "type": "color",
            "color": {
              "type": "solid",
              "r": 242,
              "g": 242,
              "b": 242,
              "a": 1
            },
            "media": null,
            "opacity": 0,
            "scale": null,
            "position": null
          },
          "shadow": {
            "color": {
              "type": "solid",
              "r": 255,
              "g": 255,
              "b": 255,
              "a": 1
            },
            "axes": {
              "x": "0",
              "y": "0",
              "b": "0"
            },
            "show": false
          }
        },
        "mobile": {
          "visible": true,
          "layout": {
            "transform": {
              "translate": "0px, 0px",
              "rotate": "0deg"
            },
            "width": "100%",
            "height": `${mobileWidgetHeight}px`
          },
          "stretched": false,
          "margin": 0,
          "padding": 0,
          "columnSpacing": 0,
          "background": null,
          "shadow": {
            "color": {
              "type": "solid",
              "r": 255,
              "g": 255,
              "b": 255,
              "a": 1
            },
            "axes": {
              "x": "0",
              "y": "0",
              "b": "0"
            },
            "show": false
          }
        }
      },
      widgets: _.times(1).map((index) => {
        return {
          type: SECTION_COLUMN,
          name: "Column",
          properties: {
            "desktop": {
              "visible": true,
              "layout": {
                "transform": {
                  "translate": "0px, 0px"
                },
                "width": `${100 / 1}%`,
                "height": null
              },
              "container": {
                "minWidth": 1200 / 1,
                "alignment": "center"
              },
              "alignment": "center",
              "background": null
            },
            "mobile": {
              "visible": true,
              "layout": {
                "transform": {
                  "translate": "0px, 0px"
                },
                "width": "100%",
                "height":  `${mobileWidgetHeight}px`
              },
              "container": {
                "minWidth": 375,
                "alignment": null
              },
              "alignment": "center",
              "background": null
            }
          }
        }
      })
    };

    dispatch(createWidget(editor, [sectionWidget], id));
  }

  const handlePaletteClick = () => {
    const paletteBtn = document.getElementById("button-palette");
    if (paletteBtn) {
      paletteBtn.click();
    }
  };

  return (
    <>
      <PageWidgetContext.Provider value={pageWidgetContext}>
        {(storyboardType === "INTERACTIVE_CONTENT" || storyboardType === "SPATIAL" || entityType === "PALETTE_COMPONENT") && (
          <Container widgetProps={widgetProps} widgetRef={widgetRef}>
            <div
              id={id}
              ref={ref}
              className={cx(className, styles.page, "cs_page_root", {
                [styles.mobile]: breakpoint === "mobile",
                [styles.optimizing]: isOptimizing
              })}
              data-storyboard-type={storyboardType}
              data-widget-id={id}
              data-widget-type={type}
              data-container={true}
              data-selectable={true}
              style={widgetStyle}
            >
              {shouldShowOptimizeLoader && storyboardType === "INTERACTIVE_CONTENT" && (
                <>
                <div className={styles.overlay}></div>
                <OptimizeLoader editor={editor} parent={widgetProps.uid} view={view} />
                </>
              )}
              {background && editorWidgetViewer && (
                <Portal mountNode={editorWidgetViewer}>
                  <BackgroundView
                    className={styles.pageBackground}
                    background={background}
                  />
                </Portal>
              )}
              {children}

              {hotspotWidget && hotspotVisibility && (
                <Widget key={hotspotWidget.uid} widgetProps={hotspotWidget} />
              )}

              {!editorContext.disabled && showToolbar && (
                <PageWidgetDesktopAction
                  editor={editor}
                  storyboardType={storyboardType}
                  breakpoint={breakpoint}
                  storyboardId={storyboardId}
                  isOptimizing={isOptimizing}
                  {...widgetProps}
                />
              )}
            </div>
          </Container>
        )}
        {SEGMENT_STORYBOARDS.includes(storyboardType) && (
          <Container widgetProps={widgetProps} widgetRef={widgetRef}>
            <div
              id={`page-${id}`}
              className={cx(
                styles.pageWidgetContainer,
                {
                  [styles.selected]: selected,
                },
                { [styles.mobileContainer]: breakpoint === "mobile" },
                { [styles.optimizing]: isOptimizing }
              )}
              style={{ height: `${widgetHeight}px`, minHeight: "50px" }}
              onClick={handleSelectPage}
              > 
              <div id={`hover-${id}`} className={cx(styles.hoverOverlay,{[styles.hide]: widgetHeight < 150})}>
                {children.length == 0 &&
                  <div className={styles.emptyContainer}>
                    <div className={styles.empty}>
                      <div className={styles.buttonGroup}>
                        <button className={styles.iconButton} onClick={handleAddSection}>
                          <i className={cx('cleverstory-add-segment-element')} />
                          <span>Add Section</span>
                        </button>
                        <button className={styles.iconButton} onClick={handlePaletteClick}>
                          <i className={cx('cleverstory-add-segment-element')} />
                          <span>Add Elements</span>
                        </button>
                      </div>
                    </div>
                  </div>
                }
              </div>
              {background && editorWidgetViewer && (
                <BackgroundView
                  className={styles.pageBackground}
                  background={background}
                />
              )}
              {shouldShowOptimizeLoader && 
                <>
                  <div className={styles.overlay}></div>
                  <OptimizeLoader editor={editor} parent={widgetProps.uid} view={view} />
                </>
              }
              <div
                id={id}
                ref={ref}
                className={cx(className, styles.page, "cs_page_root", {
                  [styles.mobile]: breakpoint === "mobile",
                })}
                data-storyboard-type={storyboardType}
                data-widget-id={id}
                data-widget-type={type}
                data-container={true}
                data-selectable={true}
                style={widgetStyle}
              >
                {children}

                {hotspotWidget && hotspotVisibility && hotspotContainer?.uid === id &&  (
                  <Widget key={hotspotWidget.uid} widgetProps={hotspotWidget} />
                )}
              </div>
              {!editorContext.disabled && (
                <PageWidgetDesktopAction
                  editor={editor}
                  showToolbar={(selected && _.size(selectedWidget) == 0)}
                  storyboardType={storyboardType}
                  breakpoint={breakpoint}
                  storyboardId={storyboardId}
                  isOptimizing={isOptimizing}
                  {...widgetProps}
                />
              )}

              {!editorContext.disabled &&
                selected &&
                _.size(selectedWidget) == 0 && (
                  <PageControllerAction
                    pageId={id}
                    pages={pages}
                    editor={editor}
                    storyboardType={storyboardType}
                    breakpoint={breakpoint}
                  />
                )
              }
            </div>
          </Container>
        )}
      </PageWidgetContext.Provider>

      {!editorContext.disabled && breakpoint === 'desktop' && INTERACTIVE_CONTENT_TYPES.includes(storyboardType) &&
        <>
          <CreatePageButton
            editorSession={editor}
            style={{
              right: `calc((100% - ${visibleWitdh}px)/2)`,
              top: `50%`
            }}
            storyboard={storyboard}
          />
        </>
      }

      {tools.gridlines && (storyboardType === "INTERACTIVE_CONTENT" ||  entityType === "PALETTE_COMPONENT") && (
        <>
          <div
            className={cx(styles.boundary, styles.top, {
              [styles.hideBoundary]:
                childrenSelection.selected && !isSelectedWidgetValid,
            })}
            style={{
              top: offset,
            }}
          />
          <div
            className={cx(styles.boundary, styles.bottom, {
              [styles.hideBoundary]:
                childrenSelection.selected && !isSelectedWidgetValid,
            })}
            style={{
              top: `${widgetHeight + offset}px`,
            }}
          />

          <div
            className={cx(styles.boundary, styles.left, {
              [styles.hideBoundary]:
                childrenSelection.selected && !isSelectedWidgetValid,
            })}
            style={{
              left: `calc((100% - ${visibleWitdh}px)/2)`,
              height: `${widgetHeight + 300}px`,
            }}
          />
          <div
            id="forGuideLineWidth"
            style={{
              width: `calc((100% - ${visibleWitdh}px)/2)`,
              left: 0,
              position: "absolute",
            }}
          ></div>
          <div
            className={cx(styles.boundary, styles.right, {
              [styles.hideBoundary]:
                childrenSelection.selected && !isSelectedWidgetValid,
            })}
            style={{
              right: `calc((100% - ${visibleWitdh}px)/2)`,
              height: `${widgetHeight + 300}px`,
            }}
          />
        </>
      )}
    </>
  );
};

const pageWidgetEquals = function (prevProps, nextProps) {
  const paths = ["className", "style", "widgetProps", "widgetRef", "children"];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
};

const PageWidget = React.memo(PageWidgetComponent, pageWidgetEquals);

const PageWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isPaletteComponentEditor =
    location.pathname.includes("paletteComponent");
  const breakpoint = _.get(props, "breakpoint");

  const [view, setView] = useState("style");
  const isSegment = SEGMENT_STORYBOARDS.includes(props.storyboardType);
  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const selectedWidget = useSelectedWidgetSelector(editorSession)?.[0];
  const aiMobileOptimizationEnabled = useSelector(state => _.get(state, 'workspaces.workspace.item.aiMobileOptimizationEnabled', false));

  const desktopToMobileOptimized = useSelector(state => _.get(state, `${editorSession}.present.mobileOptimization.pageWidgets.${props.uid}.desktopToMobileOptimized`, false));

  const storyboardId = _.get(props, 'storyboardId') || '';
  const isOptimizing = _.get(props, 'isOptimizing') || false;

  const background = _.get(props, "properties.desktop.background", null);
  const locked = _.get(props, "properties.desktop.locked", null);

  const selectedPage = useSelector((state) => {
    return _.get(state, `${editorSession}.present.selectedPage`);
  });

  const showSettings = isSegment ? selectedPage && _.size(selectedWidget) == 0 && props.uid === selectedPage : true;

  useEffect(() => {
    if (selectedPage === null) {
      return;
    }

    if (id !== selectedPage && view === "background") {
      closePopup();
    }
  }, [selectedPage]);

  useEffect(()=>{
    if(breakpoint === 'mobile' && view === 'background') {
      closePopup();
    }
  },[breakpoint])

  const id = _.get(props, "uid");
  const editMode = useSelector((state) => {
    return _.get(state, `${editorSession}.present.editMode`) === id;
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  };

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  };

  const handleAddSegment = (position) => {
    if (!editorSession) {
      return;
    }

    dispatch(createPageSegment(editorSession, id, position));
  };

  const handleAIOptimization = () => {
    if (isOptimizing) return;

    if (desktopToMobileOptimized){
      dispatch(mobileLayoutOptimize(storyboardId, props.id, editorSession, props));
    }
    else {
      dispatch(desktopToMobileOptimize(breakpoint, storyboardId, props.id, editorSession, props));
    }
  };

  return (
    <div id={id} className={cx(styles.widgetAction)}>
      {!isPaletteComponentEditor && breakpoint == "desktop" && showSettings && !locked && (
        <Property.Action
          className={cx(styles.textButton, styles.propertyAction, {
            [styles.hideAction]:
              props.storyboardType !== "INTERACTIVE_CONTENT" &&
              id !== selectedPage,
          })}
          onClick={() => changeView("background")}
        >
          Change Background
        </Property.Action>
      )}

      {!isPaletteComponentEditor && breakpoint === "mobile" && showSettings && !locked && !isOptimizing && aiMobileOptimizationEnabled && (
        <Property.Action
          className={cx(styles.textButton, styles.propertyAction, {
            [styles.hideAction]: !SEGMENT_STORYBOARDS.includes(props.storyboardType)
          })}
          onClick={handleAIOptimization}
        >
          <div className={styles.aiIconSection}>
            <Image className={styles.pulseImage} src={smartGatingIcon} width={20} height={20} />
            <Image className={styles.pulseImage2} src={smartGatingIcon} width={20} height={20} />
          </div>
        </Property.Action>
      )}

      { isSegment && ( isSegment ? selectedPage == id : (!isPaletteComponentEditor)) &&
        <TruncatedTextWithTooltip
          className={cx(styles.title)}
          content={props.name}
          maxWidth={150}
          placement="top"
        >
          {props.name}
        </TruncatedTextWithTooltip>
      }

      {!editMode && view === "background" &&  (
        <Background.Dialog
          id={id}
          value={background}
          onChange={(value) => {
            dispatch(
              updateWidgetProperty(editorSession, id, "desktop", {
                background: value,
              })
            );
          }}
          onCancel={() => {
            closePopup();
          }}
        />
      )}

      <AddSegmentButtonComponent
        pageId={id}
        active={props.showToolbar}
        handleAddSegment={handleAddSegment}
        breakpoint={breakpoint}
      />
    </div>
  );
}

const OptimizeLoader = ({ view }) => {
  const wrapperRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!wrapperRef.current) return;
  
    const baseHeight = 406;
  
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const parentHeight = entry.contentRect.height;
        const newScale = Math.min(parentHeight / baseHeight, 1);
        setScale(newScale);
      }
    });
  
    observer.observe(wrapperRef.current);
  
    return () => {
      observer.disconnect();
    };
  }, []);

  const content =
    view === "desktop"
      ? {
          title: "Optimizing Layout...",
          description:
            "Our cleverstory AI is transforming your desktop layout for mobile. Hang tight for a few more seconds!",
        }
      : {
          title: "Fine-tuning Layout...",
          description:
            "Our cleverstory AI is making quick mobile adjustments. This will only take a moment!",
        };

  return (
    <div className={styles.loaderWrapper} ref={wrapperRef}>
      <div className={styles.scaler} style={{ transform: `scale(${scale})` }}>
        <div className={styles.skeletelContainer}>
          <div className={cx(styles.skeleton, styles.title)}></div>
          <div className={styles.wrapper}>
            <div className={cx(styles.skeleton, styles.lineShort)}></div>
            <div className={cx(styles.skeleton, styles.title)}></div>
          </div>
          <div className={cx(styles.skeleton, styles.image)}></div>
          <div className={styles.buttonWrapper}>
            <div className={cx(styles.skeleton, styles.button)}></div>
          </div>
          <div className={styles.contentWrapper}>
            <video
              className={styles.optimizeLoader}
              loop
              autoPlay
              playsInline
              muted
            >
              <source src="/images/OptimizationLoader.webm" type="video/webm" />
            </video>
            <div className={styles.content}>
              <span className={styles.heading}>{content.title}</span>
              <span className={styles.description}>{content.description}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PageControllerAction = ({
  pageId,
  storyboardType,
  editor,
  breakpoint,
  pages,
}) => {
  const dispatch = useDispatch();

  if (breakpoint !== "desktop" || storyboardType == "INTERACTIVE_CONTENT") {
    return;
  }

  let currentIndex = pages.findIndex((item) => item.uid === pageId);

  const handleMoveDown = (e) => {
    e.stopPropagation();

    if (currentIndex == _.size(pages) - 1) {
      return;
    }

    dispatch(arrangeWidget(editor, pages[currentIndex], currentIndex + 1));
    scrollSegmentToView(pageId);
  };

  const handleMoveUp = (e) => {
    e.stopPropagation();

    if (currentIndex === 0) {
      return;
    }

    dispatch(arrangeWidget(editor, pages[currentIndex], currentIndex - 1));
    scrollSegmentToView(pageId);
  };

  const scrollSegmentToView = (pageId) => {
    const el = document.getElementById(`page-${pageId}`);
    const container = document.querySelector(".editor_widget_scroll_container");

    if (el && container) {
      setTimeout(() => {
        container.scrollTo({
          top: el.offsetTop,
          behavior: "smooth",
        });
      }, 100);
    }
  }

  const onDelete = () => {
    let nextPage = null;

    if (_.size(pages) <= 1) {
      return;
    }

    nextPage =
      currentIndex == _.size(pages) - 1
        ? pages[currentIndex - 1]
        : pages[currentIndex + 1];

    actionGroup.start();
    dispatch(deleteWidgets(editor, [pageId]));
    dispatch(selectPageWidget(editor, nextPage?.uid));
    actionGroup.end();
  };

  const cloneSegment = (e) => {
    e.stopPropagation();

    dispatch(clonePage(editor, pageId, 'segment'));
  };

  return (
    <div className={styles.sliderControl}>
      <div className={styles.propertyAction}>
        <Property.Action
          onClick={handleMoveUp}
          disabled={currentIndex == 0}
          className={cx(styles.iconButton, styles.previous)}
          tooltip="Move up"
        >
          <i className={cx("cleverstory-back")} />
        </Property.Action>
        <Property.Action
          onClick={handleMoveDown}
          disabled={currentIndex == _.size(pages) - 1}
          className={cx(styles.iconButton, styles.next)}
          tooltip="Move down"
        >
          <i className={cx("cleverstory-back")} />
        </Property.Action>
        <Property.Divider className={styles.divider} />
        <Property.Action
          onClick={cloneSegment}
          className={styles.iconButton}
          tooltip="Clone"
        >
          <i className={cx("cleverstory-clone")} />
        </Property.Action>
        <Property.Divider className={styles.divider} />
        <Property.Action
          disabled={_.size(pages) < 2}
          onClick={onDelete}
          className={styles.iconButton}
          tooltip="Delete"
        >
          <i className={cx("cleverstory-trash")} />
        </Property.Action>
      </div>
    </div>
  );
};

const CreatePageButton = ({ editorSession, style, storyboard }) => {
  const dispatch = useDispatch();
  const pages = useSelector(state => _.get(state, 'pages.items', []));
  let { url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  const handleCreate = () => {
    if (storyboard.type === STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2) {
      setLoading(true);
      const lastOrder = _.get(_.maxBy(pages, "order"), "order", 0);

      const request = {
        name: `Section ${_.size(pages) + 1}`,
        storyboard: storyboard.id,
        order: lastOrder + 1,
      };

      dispatch(
        createPage(request, (response) => {
          setLoading(false);
          history.push(`${baseUrl}/pages/${response.id}`);
        })
      );
      return;
    }
    dispatch(createInteractivePageSection(editorSession, 0));
  };

  return (
    <div className={styles.createPageButton} style={style}>
      <Tooltip placement='left' content={loading ? 'Adding Page': 'Add Page'}>
        <Button className={cx({ [styles.noAction]: loading })} variant="primary" disabled={loading} onClick={handleCreate}>
          {loading ? <Spinner className={styles.dot} animation="border" size="sm"/> : <i className={cx(styles.icon, "cleverstory-plus")} />}
        </Button>
      </Tooltip>
    </div>
  );
};

PageWidget.moveableConfig = (editorSession, widget) => {
  return {
    draggable: false,
    resizable: true,
    rotatable: false,
    selectable: true,
    cloneable: false,
    deletable: false,
    renderDirections: [],
    onResize: (widget, layout) => {
      const frame = new Frame(layout);

      if (frame.has("transform", "translate")) {
        frame.set("transform", "translate", "0px, 0px");
      }

      const rect = getBoundingRectFromLayout(layout);
      const height = Math.max(600, rect.height);

      frame.set("height", `${height}px`);

      return frame.get();
    },
  };
};

const PageWidgetActions = ({ editor, breakpoint, props }) => {
  if (breakpoint === "mobile") {
    return;
  }

  return (
    <PageWidgetDesktopAction
      editor={editor}
      breakpoint={breakpoint}
      {...props}
    />
  );
};

PageWidget.sizeConfig = async (widget, mediaCache) => {
  const url = _.get(widget, "properties.desktop.background.media.url", "");
  if (!url) {
    return {
      size: 0,
      type: "image",
    };
  }
  return {
    size: (await getMediaInfo(`${IMAGE_CDN_ENDPOINT}/${url}`, mediaCache)) || 0, // MB
    type: "image",
  };
};

PageWidget.processRequest = (widget) => {
  var properties = widget.properties;

  var socialIcon = _.get(properties, "desktop.social.icon", null);
  if (socialIcon) {
    properties = {
      ...properties,
      desktop: {
        ...properties.desktop,
        social: {
          ...properties.desktop.social,
          icon: socialIcon.id,
        },
      },
    };
  }

  var twitterIcon = _.get(properties, "desktop.twitter.icon", null);
  if (twitterIcon) {
    properties = {
      ...properties,
      desktop: {
        ...properties.desktop,
        twitter: {
          ...properties.desktop.twitter,
          icon: twitterIcon.id,
        },
      },
    };
  }

  var background = _.get(properties, "desktop.background");
  var media = _.get(background, "media");
  if (background && media) {
    properties = {
      ...properties,
      desktop: {
        ...properties.desktop,
        background: {
          ...background,
          media: media.id,
        },
      },
    };
  }

  return {
    ...widget,
    properties: properties,
  };
};

const AddSegmentButtonComponent = ({
  pageId,
  handleAddSegment,
  active,
  breakpoint,
}) => {
  const dispatch = useDispatch();
  const buttonRef = React.useRef(null);

  const [state, setState] = useState({
    showTop: false,
    showBottom: false,
    showResize: false,
  });

  const editorWidgetScrollContainer = document.getElementsByClassName(
    "editor-page-wrapper"
  )[0];

  const pageContainerElement = document.getElementById(`page-${pageId}`);

  useEffect(() => {
    const pageElement = document.getElementById(pageId);
    const overlay = document.getElementById(`hover-${pageId}`);
    if (!pageElement) return;

    const hoverZone = 100;

    const handlePageMouseMove = (e) => {
      const rect = pageElement.getBoundingClientRect();
      const y = e.clientY - rect.top;

      if (y <= hoverZone) {
        toggleButtonVisibility(true, false);
      } else if (y >= rect.height - hoverZone) {
        toggleButtonVisibility(false, true);
      } else {
        toggleButtonVisibility(false, false);
      }
    };

    const handleOverlayMouseMove = (e) => {
      const rect = overlay.getBoundingClientRect();
      const y = e.clientY - rect.top;

      if (y <= hoverZone) {
        toggleButtonVisibility(true, false);
      } else if (y >= rect.height - hoverZone) {
        toggleButtonVisibility(false, true);
      } else {
        toggleButtonVisibility(false, false);
      }
    };

    const handleOverlayLeave = () =>
      setState((state) => {
        return {
          ...state,
          showResize: false,
          showTop: false,
          showBottom: false,
        };
      });

    const handlePageLeave = () => {
      setState((state) => {
        return {
          ...state,
          showResize: false,
          showTop: false,
          showBottom: false,
        };
      });
    };

    pageElement.addEventListener("mousemove", handlePageMouseMove);
    pageElement.addEventListener("mouseleave", handlePageLeave);

    if (overlay) {
      overlay.addEventListener("mousemove", handleOverlayMouseMove);
      overlay.addEventListener("mouseleave", handleOverlayLeave);
    }

    return () => {
      pageElement.removeEventListener("mousemove", handlePageMouseMove);
      pageElement.removeEventListener("mouseleave", handlePageLeave);

      if (overlay) {
        overlay.removeEventListener("mousemove", handleOverlayMouseMove);
        overlay.removeEventListener("mouseleave", handleOverlayLeave);
      }
    };
  }, [pageId]);

  const toggleButtonVisibility = (top, bottom) => {
    setState({
      showTop: top,
      showBottom: bottom,
      showResize: true,
    });
  };

  const pageElement = document.getElementById(pageId);
  const pageRect = pageElement?.getBoundingClientRect();
  if (!pageRect || !editorWidgetScrollContainer) return null;

  return (
    <Portal mountNode={pageContainerElement}>
      {breakpoint == "desktop" && (
        <>
          <Button
            id="add-segment-button-top"
            name="add-segment-button-top"
            ref={buttonRef}
            size="sm"
            variant="primary"
            className={cx(styles.addSegmentButton, {
              [styles.hovering]: (state.showTop && active),
            })}
            onClick={() => {
              handleAddSegment("top");
            }}
          >
            <i className={cx(styles.icon, "cleverstory-plus")} />
            Add Frame
          </Button>
          <Button
            id="add-segment-button-bottom"
            name="add-segment-button-bottom"
            ref={buttonRef}
            style={{
              top: `${pageRect.height - 15}px`,
            }}
            size="sm"
            variant="primary"
            className={cx(styles.addSegmentButton, {
              [styles.hovering]: (state.showBottom && active),
            })}
            onClick={() => {
              handleAddSegment("bottom");
            }}
          >
            <i className={cx(styles.icon, "cleverstory-plus")} />
            Add Frame
          </Button>
        </>
      )}
      <PageResizeHandle
        pageId={pageId}
        pageRect={pageRect}
        show={state.showResize}
      />
    </Portal>
  );
};

// Alternative approach using a more direct event handler pattern:
const PageResizeHandle = ({ pageId, pageRect, show }) => {
  const dispatch = useDispatch();
  const dragStateRef = useRef(null);

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;
  const breakpoint = useSelector((state) =>
    _.get(state, `${editor}.present.breakpoint`)
  );
  const page = useWidgetSelector(editor, pageId);
  var widgetChildren = useWidgetChildrenSelector(editor, pageId) || [];
  const height =
    parseInt(_.get(page, `properties.${breakpoint}.layout.height`)) || 0;

  const onHeightChange = (newHeight) => {
    const layout = _.get(page, `properties.${breakpoint}.layout`) || {};
    dispatch(
      updateWidgetProperty(editor, pageId, breakpoint, {
        layout: {
          ...layout,
          height: `${newHeight}px`,
        },
      })
    );
  };

  const handleBottomResizeStart = (e) => {
    e.stopPropagation();
    e.preventDefault();

    let maxBottom = 0;
    let maxChildId = null;

    widgetChildren.forEach((child) => {
      const transform = _.get(child, `properties.${breakpoint}.layout.transform.translate`, "");
      const visible = _.get(child, `properties.${breakpoint}.visible`, true);

      if (!visible) {
        return;
      }

      if (transform) {
        const parts = transform.split(",");
        const y = parseFloat(parts[1]?.replace("px", "").trim()) || 0;

        const element = document.getElementById(child?.uid);
        if (element) {
          const rect = element.getBoundingClientRect();
          const bottom = y + rect.height;

          if (bottom > maxBottom) {
            maxBottom = bottom;
            maxChildId = child?.uid;
          }
        }
      }
    });

    let boundary = maxBottom;
    actionGroup.start();
  
    dragStateRef.current = {
      startY: e.clientY,
      startHeight: height,
      onMouseMove: (moveEvent) => {
        if (!dragStateRef.current) return;

        const deltaY = moveEvent.clientY - dragStateRef.current.startY;
        let newHeight = dragStateRef.current.startHeight + deltaY;
        let prevHeight = newHeight;
        newHeight = Math.max(50, newHeight);

        if (boundary > 0 && newHeight < boundary) {
          return; // do nothing
        }

        const widget = document.getElementById(pageId);
        if (widget) {
          widget.style.height = `${newHeight}px`;
        }
        if (prevHeight >= 50) {
          onHeightChange(newHeight);
        }
      },
      onMouseUp: () => {
        if (!dragStateRef.current) return;

        const widget = document.getElementById(pageId);
        if (widget) {
          let finalHeight = parseInt(widget.style.height, 10) || 0;

          if (boundary > 0 && finalHeight < boundary) {
            finalHeight = boundary;
            widget.style.height = `${finalHeight}px`;
            onHeightChange(finalHeight);
          }
        }
        document.removeEventListener(
          "mousemove",
          dragStateRef.current.onMouseMove
        );
        document.removeEventListener("mouseup", dragStateRef.current.onMouseUp);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";

        dragStateRef.current = null;

        actionGroup.end();
      },
    };

    document.addEventListener("mousemove", dragStateRef.current.onMouseMove);
    document.addEventListener("mouseup", dragStateRef.current.onMouseUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "ns-resize";
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (dragStateRef.current) {
        document.removeEventListener(
          "mousemove",
          dragStateRef.current.onMouseMove
        );
        document.removeEventListener("mouseup", dragStateRef.current.onMouseUp);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      }
    };
  }, []);

  const widgetVisibility = widgetChildren.map((child) => _.get(child, `properties.${breakpoint}.visible`, true)).join(",");

  useEffect(() => {
    let maxBottom = 0;

    widgetChildren.forEach((child) => {
      const transform = _.get(child, `properties.${breakpoint}.layout.transform.translate`, "");
      const visible = _.get(child, `properties.${breakpoint}.visible`, true);
      if (!visible) return;

      if (transform) {
        const parts = transform.split(",");
        const y = parseFloat(parts[1]?.replace("px", "").trim()) || 0;

        const element = document.getElementById(child?.uid);
        if (element) {
          const rect = element.getBoundingClientRect();
          const bottom = y + rect.height;
          if (bottom > maxBottom) {
            maxBottom = bottom;
          }
        }
      }
    });

    if (maxBottom > 0) {
      const widget = document.getElementById(pageId);
      if (widget) {
        const currentHeight = parseInt(widget.style.height, 10) || 0;
        if (currentHeight < maxBottom) {
          widget.style.height = `${maxBottom}px`;
          onHeightChange(maxBottom);
        }
      }
    }
  }, [widgetVisibility]);

  return (
    <Button
      id="resize-button"
      name="resize-button"
      onMouseDown={handleBottomResizeStart}
      style={{
        top: `${pageRect.height - 15}px`,
        left: `70%`,
      }}
      size="sm"
      variant="primary"
      className={cx(styles.resizeButton, {
        [styles.hovering]: show,
      })}
    >
      <i className={cx(styles.icon, "cleverstory-stretch")} />
    </Button>
  );
};

export { PageWidget };